import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ANIMATION_URLS } from "../../utils/constants";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import { Home, PackageCheck, ShoppingBag } from "lucide-react";
import { useVendorStore } from "../../context/VendorStoreContext";

const SuccessPage = () => {
  const { state } = useLocation();
  const { storeSlug } = useVendorStore();
  const shopPath = storeSlug ? `/${storeSlug}` : "/shop";
  const { orderId, amount, products, customer } = state || {};
  const [showConfirmation, setShowConfirmation] = useState(true);

  useEffect(() => {
    const timer = window.setTimeout(() => setShowConfirmation(false), 2500);
    return () => window.clearTimeout(timer);
  }, []);

  const orderItems = useMemo(() => (Array.isArray(products) ? products : []), [products]);
  const subtotal = orderItems.reduce(
    (sum, item) => sum + Number(item.price || 0) * Number(item.quantity || 0),
    0,
  );
  const totalPaid = Number.isFinite(Number(amount)) ? Number(amount) / 100 : subtotal;
  const isPickup = customer?.receivingMethod === "pickup";

  if (!orderId) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-stone-50 px-4 text-center text-stone-600">
        Invalid order. Please return to home.
      </div>
    );
  }

  if (showConfirmation) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-emerald-50 via-white to-orange-50 px-4 py-16">
        <div className="w-full max-w-md rounded-3xl border border-emerald-100 bg-white p-8 text-center shadow-2xl shadow-emerald-100/70">
          <div className="relative mx-auto flex h-24 w-24 items-center justify-center">
            <span className="absolute h-20 w-20 animate-ping rounded-full bg-emerald-200/70" />
            <span className="absolute h-24 w-24 rounded-full bg-emerald-50" />
            <svg
              className="relative h-20 w-20 drop-shadow-sm"
              viewBox="0 0 64 64"
              fill="none"
              aria-hidden="true"
            >
              <circle
                cx="32"
                cy="32"
                r="26"
                stroke="#10b981"
                strokeWidth="5"
                strokeLinecap="round"
                strokeDasharray="166"
                strokeDashoffset="166"
              >
                <animate
                  attributeName="stroke-dashoffset"
                  from="166"
                  to="0"
                  dur="0.55s"
                  begin="0.05s"
                  fill="freeze"
                />
              </circle>
              <path
                d="M20 33.5 28.2 41 44.5 24"
                stroke="#059669"
                strokeWidth="5.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeDasharray="48"
                strokeDashoffset="48"
              >
                <animate
                  attributeName="stroke-dashoffset"
                  from="48"
                  to="0"
                  dur="0.45s"
                  begin="0.45s"
                  fill="freeze"
                />
              </path>
            </svg>
          </div>
          <p className="mt-6 text-sm font-bold uppercase tracking-[0.28em] text-emerald-700">
            Confirmed
          </p>
          <h1 className="mt-2 text-3xl font-extrabold tracking-tight text-stone-900">
            Your order is placed
          </h1>
          <p className="mt-3 text-sm leading-6 text-stone-600">
            We received your order and are preparing the summary for you.
          </p>
          <div className="mt-7 h-1.5 overflow-hidden rounded-full bg-stone-100">
            <div className="h-full w-2/3 animate-pulse rounded-full bg-gradient-to-r from-emerald-500 to-orange-400" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50/80 via-white to-stone-100 px-4 py-8 sm:py-12">
      <div className="mx-auto max-w-5xl">
        <div className="mb-6 overflow-hidden rounded-3xl border border-stone-200/80 bg-white shadow-xl shadow-orange-100/50">
          <div className="grid gap-6 p-6 md:grid-cols-[0.9fr_1.1fr] md:items-center md:p-8">
            <div className="mx-auto w-full max-w-xs">
            <DotLottieReact
              src={ANIMATION_URLS.orderFullfilment}
              background="transparent"
              speed={1}
              loop
              autoplay
            />
            </div>
            <div className="text-center md:text-left">
              <span className="inline-flex items-center gap-2 rounded-full bg-emerald-100 px-3 py-1 text-xs font-bold uppercase tracking-wide text-emerald-800">
                <PackageCheck className="h-4 w-4" />
                Order confirmed
              </span>
              <h1 className="mt-4 text-3xl font-extrabold tracking-tight text-stone-900 sm:text-4xl">
                Thank you for your order
              </h1>
              <p className="mt-3 text-sm leading-6 text-stone-600 sm:text-base">
                Your items are being packed and you will receive updates on your registered contact.
              </p>
              <p className="mt-4 break-all rounded-2xl bg-stone-50 px-4 py-3 text-sm text-stone-600 ring-1 ring-stone-200">
                Order ID: <span className="font-bold text-stone-900">{orderId}</span>
              </p>
            </div>
          </div>
        </div>

        <div className="grid gap-6 md:grid-cols-[0.9fr_1.1fr]">
          <div className="rounded-3xl border border-stone-200/80 bg-white p-6 shadow-lg shadow-stone-200/50">
            <h2 className="text-lg font-extrabold text-stone-900">
              {isPickup ? "Pickup details" : "Delivery details"}
            </h2>
            <div className="mt-4 space-y-2 text-sm leading-6 text-stone-600">
              <p className="font-semibold text-stone-900">{customer?.fullName || "Customer"}</p>
              {isPickup ? (
                <p>Self pickup selected. Delivery fee has been waived for this order.</p>
              ) : (
                <>
                  <p>{customer?.address}</p>
                  <p>
                    {[customer?.city, customer?.state, customer?.country]
                      .filter(Boolean)
                      .join(", ")}
                  </p>
                </>
              )}
              {customer?.phone ? <p>Phone: {customer.phone}</p> : null}
              {customer?.email ? <p>Email: {customer.email}</p> : null}
            </div>
          </div>

          <div className="rounded-3xl border border-stone-200/80 bg-white p-6 shadow-lg shadow-stone-200/50">
            <div className="mb-5 flex items-center justify-between gap-3">
              <h2 className="text-lg font-extrabold text-stone-900">Order summary</h2>
              <span className="rounded-full bg-orange-100 px-3 py-1 text-xs font-bold text-orange-800">
                {orderItems.reduce((n, item) => n + Number(item.quantity || 0), 0)} items
              </span>
            </div>
            <div className="divide-y divide-stone-100">
              {orderItems.map((item, idx) => (
                <div key={`${item.name}-${idx}`} className="flex justify-between gap-4 py-3">
                  <div className="min-w-0">
                    <p className="font-semibold text-stone-900">{item.name}</p>
                    <p className="mt-0.5 text-sm text-stone-500">
                      Qty: {item.quantity}
                    </p>
                  </div>
                  <div className="shrink-0 text-right font-bold tabular-nums text-stone-900">
                    ₹{(Number(item.price || 0) * Number(item.quantity || 0)).toFixed(0)}
                  </div>
                </div>
              ))}
              <div className="flex justify-between gap-4 pt-4 text-sm text-stone-600">
                <span>Items total</span>
                <span className="font-semibold tabular-nums text-stone-800">
                  ₹{subtotal.toFixed(0)}
                </span>
              </div>
              <div className="mt-3 flex items-baseline justify-between gap-4 rounded-2xl bg-orange-50 px-4 py-3">
                <span className="font-extrabold text-stone-900">Total paid</span>
                <span className="text-2xl font-extrabold tabular-nums text-orange-600">
                  ₹{totalPaid.toFixed(0)}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8 flex flex-col gap-3 text-center sm:flex-row sm:justify-center">
          <Link
            to={shopPath}
            className="inline-flex items-center justify-center gap-2 rounded-2xl bg-stone-900 px-6 py-3 text-sm font-bold text-white shadow-lg shadow-stone-300/50 transition hover:bg-stone-800"
          >
            <ShoppingBag className="h-4 w-4" />
            Continue Shopping
          </Link>
          <Link
            to="/"
            className="inline-flex items-center justify-center gap-2 rounded-2xl border-2 border-stone-200 bg-white px-6 py-3 text-sm font-bold text-stone-700 transition hover:bg-stone-50"
          >
            <Home className="h-4 w-4" />
            Go Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default SuccessPage;
